import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Form,
  Input,
  Button,
  Tag,
  Image,
  Space,
  Select,
  Modal,
  message,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTable } from '@/hooks/useTable';
import {
  getNewcomerChallengeList,
  deleteNewcomerChallenge,
  getNewcomerChallengeDetail,
  getAwardTypes,
  type AwardType,
} from './api';
import type {
  NewcomerChallengeListParams,
  NewcomerChallenge,
} from './types';
import dayjs from 'dayjs';
import NewcomerChallengeDrawer from './NewcomerChallengeDrawer';

const STATUS_MAP: Record<number, { text: string; color: string }> = {
  0: { text: '未开始', color: 'default' },
  1: { text: '投稿中', color: 'processing' },
  2: { text: '评比中', color: 'warning' },
  3: { text: '已结束', color: 'success' },
};

const NewcomerChallengePage: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<NewcomerChallenge>();
  const [searchParams, setSearchParams] = useState<NewcomerChallengeListParams>({
    currentPage: 1,
    pageSize: 10,
  });
  const [awardTypes, setAwardTypes] = useState<AwardType[]>([]);

  // 加载奖励类型列表
  useEffect(() => {
    getAwardTypes()
      .then((res) => {
        if ((res.status === 200 || res.success) && res.data && Array.isArray(res.data)) {
          setAwardTypes(res.data);
        }
      })
      .catch(() => {
        // 静默失败，不影响列表显示
      });
  }, []);

  const {
    data,
    isLoading,
    handleTableChange,
    updateParams,
    currentPage,
    pageSize,
    total,
    refresh,
  } = useTable({
    queryKey: 'newcomer-challenge-list',
    queryFn: async (current, size, params) => {
      const response = await getNewcomerChallengeList({
        ...params,
        currentPage: current,
        pageSize: size,
      });
      return response.data;
    },
    defaultPageSize: 10,
    defaultParams: searchParams,
  });

  const handleSearch = async () => {
    const values = await form.validateFields();
    setSearchParams({ ...searchParams, ...values });
    updateParams({ ...searchParams, ...values });
  };

  const handleReset = () => {
    form.resetFields();
    setSearchParams({
      currentPage: 1,
      pageSize: 10,
    });
    updateParams({
      currentPage: 1,
      pageSize: 10,
    });
  };

  const handleCreate = () => {
    setEditingActivity(undefined);
    setIsDrawerOpen(true);
  };

  const handleEdit = async (record: NewcomerChallenge) => {
    const res = await getNewcomerChallengeDetail(record.id!);
    if (res.status !== 200) return;
    setEditingActivity(res.data);
    setIsDrawerOpen(true);
  };

  const handleDelete = (record: NewcomerChallenge) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除活动"${record.title}"吗？`,
      onOk: async () => {
        const res = await deleteNewcomerChallenge(record.id!);
        if (res.status === 200) {
          message.success('删除成功');
          refresh();
        } else {
          message.error(res.message || '删除失败');
        }
      },
    });
  };

  const handleViewSubmissions = (id: number, status?: number) => {
    navigate(`/newcomer-challenge/submissions/${id}`, {
      state: { status },
    });
  };

  const handleViewDetail = (id: number) => {
    navigate(`/newcomer-challenge/detail/${id}`);
  };

  const handleDrawerSuccess = () => {
    refresh();
    setIsDrawerOpen(false);
    setEditingActivity(undefined);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setEditingActivity(undefined);
  };

  const columns = [
    {
      title: 'RookieKingId',
      dataIndex: 'id',
      key: 'id',
      width: 100,
    },
    {
      title: '活动封面',
      dataIndex: 'previousCoverJa',
      key: 'previousCoverJa',
      width: 100,
      render: (url: string) => (
        <Image
          src={url}
          alt="活动封面"
          style={{ width: 80, height: 80, objectFit: 'cover' }}
        />
      ),
    },
    {
      title: '活动标题',
      key: 'title',
      width: 200,
      render: (_: any, record: NewcomerChallenge) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{record.title}</div>
          <div style={{ color: '#666' }}>{record.subtitle}</div>
        </div>
      ),
    },
    {
      title: '投稿时间',
      key: 'submitTime',
      width: 200,
      render: (_: any, record: NewcomerChallenge) => (
        <div style={{ whiteSpace: 'nowrap' }}>
          <div>
            开始：{dayjs(record.submitBeginTime).format('YYYY-MM-DD HH:mm')}
          </div>
          <div>
            结束：{dayjs(record.submitEndTime).format('YYYY-MM-DD HH:mm')}
          </div>
        </div>
      ),
    },
    {
      title: '3日奖励发放',
      key: 'reward3Days',
      width: 120,
      render: (_: any, record: NewcomerChallenge) => {
        const rewardSubList = record.rewardSubList || [];
        // 3日奖励：dayType === 3
        const reward3Days = rewardSubList.filter((r) => r.dayType === 3);
        if (reward3Days.length === 0) return '-';
        
        // 从奖励类型列表中构建 typeMap
        const typeMap: Record<number, string> = {};
        awardTypes.forEach((item) => {
          typeMap[item.id] = item.typeName || `类型${item.id}`;
        });
        
        return (
          <div>
            {reward3Days.map((r, index) => (
              <div key={index}>
                {typeMap[r.type] || `类型${r.type}`}: {r.winnerCount}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: '7日奖励发放',
      key: 'reward7Days',
      width: 120,
      render: (_: any, record: NewcomerChallenge) => {
        const rewardSubList = record.rewardSubList || [];
        // 7日奖励：dayType === 7
        const reward7Days = rewardSubList.filter((r) => r.dayType === 7);
        if (reward7Days.length === 0) return '-';
        
        // 从奖励类型列表中构建 typeMap
        const typeMap: Record<number, string> = {};
        awardTypes.forEach((item) => {
          typeMap[item.id] = item.typeName || `类型${item.id}`;
        });
        
        return (
          <div>
            {reward7Days.map((r, index) => (
              <div key={index}>
                {typeMap[r.type] || `类型${r.type}`}: {r.winnerCount}
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 60,
      render: (status: number) => (
        <Tag color={STATUS_MAP[status]?.color}>{STATUS_MAP[status]?.text}</Tag>
      ),
    },
    {
      title: '创建人',
      dataIndex: 'updateUserName',
      key: 'updateUserName',
      width: 120,
      render: (value: string) => value || '-',
    }, 
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: NewcomerChallenge) => (
        <Space>
          <Button
            type="link"
            onClick={() => handleViewSubmissions(record.id!, record.status)}
          >
            查看投稿
          </Button>
          <Button type="link" onClick={() => handleEdit(record)}>
            {/* {record.status === 0 ? '编辑' : '查看'} */}
            编辑
          </Button>
          <Button type="link" danger onClick={() => handleDelete(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="新人王挑战赛"
      extra={
        <Button type="primary" onClick={handleCreate}>
          创建活动
        </Button>
      }
    >
      <div style={{ marginBottom: 16 }}>
        <Form form={form} layout="inline">
          <Form.Item name="id">
            <Input placeholder="请输入活动ID" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="title">
            <Input placeholder="请输入活动标题" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="updateUserName">
            <Input placeholder="请输入创建人" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item name="status">
            <Select
              style={{ width: 160 }}
              placeholder="请选择状态"
              allowClear
              options={Object.entries(STATUS_MAP).map(([value, { text }]) => ({
                label: text,
                value: Number(value),
              }))}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" onClick={handleSearch}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          showQuickJumper: true,
        }}
        loading={isLoading}
        onChange={handleTableChange}
        scroll={{ x: 'max-content' }}
      />

      {isDrawerOpen && (
        <NewcomerChallengeDrawer
          visible={isDrawerOpen}
          editData={editingActivity}
          onSuccess={handleDrawerSuccess}
          onClose={handleDrawerClose}
        />
      )}
    </Card>
  );
};

export default NewcomerChallengePage;
